import {
	Alert,
	Box,
	CircularProgress,
	Dialog,
	FormControl,
	MenuItem,
	TextField,
} from "@mui/material";
import { Button } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { addDays, format, isAfter, isSameDay, startOfDay } from "date-fns";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import DialogWrapper from "../../../../../design/dialog-wrapper";
import useWorkersData from "../../../controllers/use-workers-data";
import {
	futurePatternChange,
	resetFuturePatternChange,
} from "../../../store/manage-workers-slice";

interface SchedulePatternChangeDialogProps {
	open: boolean;
	handleClose: () => void;
	workerId: string;
	currentShiftPatternId: number;
	locationShiftPatterns: any[];
}

const SchedulePatternChangeDialog: React.FC<SchedulePatternChangeDialogProps> = ({
	open,
	handleClose,
	workerId,
	currentShiftPatternId,
	locationShiftPatterns,
}) => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const {
		futurePatternChangeStatus,
		futurePatternChangeError,
		hasLeaveConflicts,
		leaveConflictCount,
	} = useWorkersData();

	const [selectedPatternId, setSelectedPatternId] = useState<number | "">("");
	const [effectiveDate, setEffectiveDate] = useState<Date | null>(
		addDays(startOfDay(new Date()), 1)
	);
	const [reason, setReason] = useState("");
	const [showConflicts, setShowConflicts] = useState(false);

	const handleCloseAndReset = React.useCallback(() => {
		dispatch(resetFuturePatternChange());
		handleClose();
	}, [dispatch, handleClose]);

	useEffect(() => {
		if (open) {
			setSelectedPatternId("");
			setEffectiveDate(addDays(startOfDay(new Date()), 1));
			setReason("");
			setShowConflicts(false);
			dispatch(resetFuturePatternChange());
		}
	}, [open, dispatch]);

	useEffect(() => {
		if (futurePatternChangeStatus === "fulfilled") {
			if (hasLeaveConflicts) {
				setShowConflicts(true);
			} else {
				handleCloseAndReset();
			}
		}
	}, [futurePatternChangeStatus, hasLeaveConflicts, handleCloseAndReset]);

	const handleSave = () => {
		if (selectedPatternId && effectiveDate) {
			dispatch(
				futurePatternChange({
					workerId,
					shiftPatternId: Number(selectedPatternId),
					effectiveDate: format(effectiveDate, "yyyy-MM-dd"),
					reason,
				})
			);
		}
	};

	const isDateValid = effectiveDate && isAfter(effectiveDate, startOfDay(new Date()));
	const isPatternDifferent = selectedPatternId !== currentShiftPatternId;
	const isFormValid = selectedPatternId !== "" && isDateValid && isPatternDifferent;

	return (
		<Dialog open={open} onClose={handleCloseAndReset} maxWidth='sm' fullWidth>
			<DialogWrapper
				dialogTitle={t("workers_schedule-pattern-change")}
				dialogContent={
					<Box display='flex' flexDirection='column' gap={3} mt={1}>
						{futurePatternChangeStatus === "error" && (
							<Alert severity='error'>{futurePatternChangeError}</Alert>
						)}

						{showConflicts && (
							<Alert severity='warning'>
								{t("workers_leave-conflicts-warning", {
									count: leaveConflictCount,
								})}
								<Box mt={1}>
									<Button onClick={handleCloseAndReset} variant='contained' color='primary' size='small'>
										{t("workers_acknowledge-and-close")}
									</Button>
								</Box>
							</Alert>
						)}

						{!showConflicts && (
							<>
								<FormControl fullWidth variant='filled'>
									<TextField
										select
										label={t("workers_new-shift-pattern")}
										variant='filled'
										value={selectedPatternId}
										onChange={(e) => setSelectedPatternId(Number(e.target.value))}
										error={selectedPatternId !== "" && !isPatternDifferent}
										helperText={
											selectedPatternId !== "" && !isPatternDifferent
												? t("workers_select-different-pattern")
												: ""
										}
									>
										{locationShiftPatterns.filter((p) => p.id !== currentShiftPatternId).length > 0 ? (
											locationShiftPatterns
												.filter((p) => p.id !== currentShiftPatternId)
												.map((lsp: any) => (
													<MenuItem value={lsp.id} key={lsp.id}>
														{lsp.name}
													</MenuItem>
												))
										) : (
											<MenuItem disabled value=''>
												{t("workers_no-options")}
											</MenuItem>
										)}
									</TextField>
								</FormControl>

								<LocalizationProvider dateAdapter={AdapterDateFns}>
									<DatePicker
										label={t("workers_effective-date")}
										value={effectiveDate}
										onChange={(newValue) => setEffectiveDate(newValue)}
										minDate={addDays(startOfDay(new Date()), 1)}
										renderInput={(params) => (
											<TextField {...params} variant='filled' fullWidth />
										)}
									/>
								</LocalizationProvider>

								<TextField
									label={t("workers_reason-optional")}
									variant='filled'
									multiline
									rows={3}
									value={reason}
									onChange={(e) => setReason(e.target.value)}
									inputProps={{ maxLength: 255 }}
								/>
							</>
						)}
					</Box>
				}
				actionButtons={
					!showConflicts ? (
						<Box display='flex' gap={1} mr={1}>
							<Button onClick={handleCloseAndReset} variant='outlined'>
								{t("common_cancel")}
							</Button>
							<Button
								onClick={handleSave}
								variant='contained'
								color='primary'
								disabled={!isFormValid || futurePatternChangeStatus === "pending"}
								startIcon={
									futurePatternChangeStatus === "pending" ? (
										<CircularProgress size={20} color='inherit' />
									) : null
								}
							>
								{futurePatternChangeStatus === "pending"
									? t("workers_scheduling")
									: t("workers_schedule-change")}
							</Button>
						</Box>
					) : undefined
				}
			/>
		</Dialog>
	);
};

export default SchedulePatternChangeDialog;
